import { QuestionItem } from '../types/form';

type VisibilityRule = NonNullable<QuestionItem['visibilityRules']>[number];

/** One rule against the current answers. Shared by both evaluators below. */
const ruleMatches = (
  rule: VisibilityRule,
  formData: Record<string, any>
): boolean => {
  // Determine the value of the trigger question from formData.
  // Frontend keys might be `q_{id}` or just `{id}` or custom `name`.
  // We try multiple conventions to find the value.
  const triggerId = rule.triggerQuestionId;
  
  // 1. Try key "q_{id}" (Common convention in StepperForm)
  let actualValue = formData[`q_${triggerId}`];

  // 2. If undefined, try direct ID string
  if (actualValue === undefined) {
    actualValue = formData[String(triggerId)];
  }
  
  // 3. Note: If the question has a custom 'name' (fieldName), we might miss it here 
  // without a map of ID -> Name. 
  // TODO: Pass a lookup map if 'name' usage is common and critical.
  
  // If value is missing completely, treat as empty string or null
  if (actualValue === undefined || actualValue === null) {
    actualValue = '';
  }

  const expectedValue = rule.triggerValue || '';

  // Convert boolean values to strings for comparison if needed
  const strActual = String(actualValue).toLowerCase();
  const strExpected = String(expectedValue).toLowerCase();

  switch (rule.operator) {
    case 'eq':
      return strActual === strExpected;
    case 'neq':
      return strActual !== strExpected;
    case 'gt':
      return Number(actualValue) > Number(expectedValue);
    case 'lt':
      return Number(actualValue) < Number(expectedValue);
    case 'contains':
      return strActual.includes(strExpected);
    default:
      // Unknown operator, safe default is true (show) or false (hide)?
      // Usually safe to show to avoid blocking progress
      console.warn(`Unknown operator: ${rule.operator}`);
      return true;
  }
};

/**
 * Evaluates whether a question should be visible based on its visibility rules and current form data.
 * @param question The question to evaluate
 * @param formData The current state of form answers
 * @returns true if the question should be shown, false otherwise
 */
export const evaluateVisibility = (
  question: QuestionItem,
  formData: Record<string, any>,
  visibleIds?: Set<number>
): boolean => {
  // When the form-wide, transitive answer is available, it decides
  // (client worklist #84). Without it, behaviour is exactly as before.
  if (visibleIds) return visibleIds.has(Number(question.id));

  // If no rules, it's visible by default
  if (!question.visibilityRules || question.visibilityRules.length === 0) {
    return true;
  }

  // Rules in the same logic group are ANDed. Separate groups are ORed.
  // Existing configurations use only group 0, so their AND behavior is preserved.
  const groups = new Map<number, NonNullable<QuestionItem['visibilityRules']>>();
  question.visibilityRules.forEach((rule) => {
    const group = rule.logicGroup ?? 0;
    groups.set(group, [...(groups.get(group) ?? []), rule]);
  });

  return Array.from(groups.values()).some((rules) =>
    rules.every((rule) => ruleMatches(rule, formData))
  );
};

/**
 * Every visible question, following dependencies through (client worklist
 * #84). Mirrors visibleQuestionIds in Portal-Backend/services/
 * formVisibility.service.js; both run the cases in
 * src/utils/__fixtures__/visibilityCases.json.
 *
 * A rule matches only if its trigger question is itself visible, so a
 * follow-up cannot be shown by an answer to a question no longer being asked —
 * after answering No to children, the stale count no longer shows the
 * children's details. Not "treat a hidden answer as empty": with a `neq` rule
 * that would itself SHOW a follow-up. A trigger outside `questions`, or a
 * dependency cycle, counts as visible — err towards showing.
 */
export function visibleQuestionIds(
  questions: QuestionItem[],
  formData: Record<string, any>
): Set<number> {
  const byId = new Map(questions.map((q) => [Number(q.id), q]));
  const memo = new Map<number, boolean>();
  const inProgress = new Set<number>();

  const isVisible = (id: number): boolean => {
    if (memo.has(id)) return memo.get(id) as boolean;
    const question = byId.get(id);
    if (!question) return true;
    if (inProgress.has(id)) return true;
    inProgress.add(id);

    const rules = question.visibilityRules ?? [];
    let visible = true;
    if (rules.length > 0) {
      const groups = new Map<number, VisibilityRule[]>();
      rules.forEach((rule) => {
        const group = rule.logicGroup ?? 0;
        groups.set(group, [...(groups.get(group) ?? []), rule]);
      });
      visible = Array.from(groups.values()).some((group) =>
        group.every(
          (rule) =>
            isVisible(Number(rule.triggerQuestionId)) && ruleMatches(rule, formData)
        )
      );
    }

    inProgress.delete(id);
    memo.set(id, visible);
    return visible;
  };

  const visible = new Set<number>();
  questions.forEach((q) => {
    if (isVisible(Number(q.id))) visible.add(Number(q.id));
  });
  return visible;
}

